import { preloadAudio } from '../audio';
import { getMaterial } from '../materials';
import { SHIRT_GLB_URL } from '../locker/lockerUrls';
import { PLAYER_KICK_GLB_URL, CAPTAIN_TSUBASA_GLB_URL } from '../characters/characterUrls';
import { FOOTBALL_GLB_URL, FOOTBALL_GOAL_GLB_URL } from '../shot/shotUrls';

export type PreloadProgress = {
  progress: number;
  label: string;
};

type ProgressFn = (p: PreloadProgress) => void;

const BAKE_KEYS = [
  'jerseyFabric',
  'pitchGrass',
  'tunnelConcrete',
  'ballLeather',
  'floodMetal',
  'shorts',
  'skin',
  'pitchLine',
] as const;

function yieldToMain() {
  return new Promise<void>((resolve) => {
    window.setTimeout(() => resolve(), 0);
  });
}

/**
 * Boot prewarm: audio, locker jersey, kick/ball/goal GLBs, material bake.
 * Kick GLB is loaded here so the penalty strike never Suspense-skips mid-shot.
 * Rapier is NOT warmed here — only used if phase === 'run'.
 */
export async function preloadGame(
  onProgress: ProgressFn = () => {},
): Promise<void> {
  const params =
    typeof location !== 'undefined'
      ? new URLSearchParams(location.search)
      : null;
  if (params?.get('prewarm') === '0') {
    onProgress({ progress: 1, label: 'Skip prewarm' });
    return;
  }

  onProgress({ progress: 0.04, label: 'Preparing Matchday…' });
  try {
    await preloadAudio();
  } catch {
    /* soft */
  }
  await yieldToMain();

  onProgress({ progress: 0.12, label: 'Loading Kit…' });
  try {
    const { useGLTF } = await import('@react-three/drei');
    useGLTF.preload(SHIRT_GLB_URL);
  } catch {
    /* soft */
  }
  await yieldToMain();

  // Penalty strike assets — must be ready before ShotAim or the kick skips
  onProgress({ progress: 0.22, label: 'Warming Up Striker…' });
  try {
    const { useGLTF } = await import('@react-three/drei');
    useGLTF.preload(PLAYER_KICK_GLB_URL);
    useGLTF.preload(CAPTAIN_TSUBASA_GLB_URL);
    useGLTF.preload(FOOTBALL_GLB_URL);
    useGLTF.preload(FOOTBALL_GOAL_GLB_URL);
  } catch {
    /* soft */
  }
  await yieldToMain();

  try {
    if (document.fonts?.ready) {
      await Promise.race([
        document.fonts.ready,
        new Promise((r) => setTimeout(r, 1500)),
      ]);
    }
  } catch {
    /* soft */
  }
  await yieldToMain();

  const storyLabels = [
    'Printing Jersey…',
    'Preparing Floodlights…',
    'Checking Goal Line Technology…',
    'Warming Up Goalkeeper…',
    'Opening Stadium Gates…',
    'Lining the Pitch…',
    'Tuning Crowd Mics…',
    'Ready for Kickoff…',
  ];

  onProgress({ progress: 0.4, label: storyLabels[0] });
  for (let i = 0; i < BAKE_KEYS.length; i++) {
    try {
      getMaterial(BAKE_KEYS[i]);
    } catch {
      /* soft */
    }
    onProgress({
      progress: 0.4 + (0.5 * (i + 1)) / BAKE_KEYS.length,
      label: storyLabels[i] ?? storyLabels[storyLabels.length - 1],
    });
    await yieldToMain();
  }

  onProgress({ progress: 0.95, label: 'Opening Stadium Gates…' });
  await yieldToMain();

  onProgress({ progress: 1, label: 'Ready' });
  await yieldToMain();
}
